import {
  Color,
  colors,
  PaletteColor,
  Select,
  Theme as MUITheme,
  IconButtonProps,
  createTheme,
  SxProps,
} from "@mui/material";
import typography from "./typography";
import { error, grey, info, patternLink, patternSecondary, primary, success, tertiary, warning } from "./palette";
import MuiIconButton from "theme/overrides/MuiIconButton";
import MuiButton from "theme/overrides/MuiButton";
import { MuiTableCell, MuiTableHead, MuiTableRow } from "theme/overrides/MuiTable";
import { MuiDrawer } from "theme/overrides/MuiDrawer";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { CSSProperties } from "react";

export type Theme = MUITheme;

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    default: Palette["primary"];
    tertiary: Palette["primary"];
    patternSecondary: Palette["primary"];
  }
  interface PaletteOptions {
    default: PaletteOptions["primary"];
    tertiary: PaletteOptions["primary"];
    patternSecondary: PaletteOptions["primary"];
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    patternSecondary: Palette["primary"];
  }

  interface PaletteOptions {
    patternSecondary: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    patternSecondary: true;
    default: true;
  }
}

function toPaletteColor(color: Color): PaletteColor {
  return {
    light: color["200"],
    main: color["500"],
    dark: color["800"],
    contrastText: color.A100,
  };
}

const theme = createTheme({
  palette: {
    primary: toPaletteColor(primary),
    // secondary: toPaletteColor(secondary),
    patternSecondary: toPaletteColor(patternSecondary),
    tertiary,
    grey,
    success,
    error,
    info,
    warning,
    default: {
      light: grey[100],
      main: grey[400],
      dark: grey[900],
      contrastText: "#fff",
    },
  },
  typography,
  mixins: {
    linkLike: {
      color: patternLink + "!important",
      fontWeight: "bold",
      "&:hover": {
        textDecoration: "underline",
        cursor: "pointer",
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size: "small",
        variant: "outlined",
        color: "primary",
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "&.Mui-selected.Mui-selected": {
            backgroundColor: "rgba(111, 150, 253, 0.4)",
          },
        },
      },
    },
    MuiIconButton,
    MuiButton,
    MuiTableCell,
    MuiTableRow,
    MuiTableHead,
    MuiDrawer,
    MuiTable: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
  },
});

theme.shadows[1] = "0px 6px 12px rgba(29, 39, 65, 0.12)";
theme.shadows[2] = "0px 8px 24px rgba(29, 39, 65, 0.12)";
theme.shadows[3] = "0px 12px 32px rgba(29, 39, 65, 0.14)";
theme.shadows[4] = "0px 8px 40px rgba(29, 39, 65, 0.14)";
theme.shadows[5] = "0px 8px 60px rgba(29, 39, 65, 0.14)";
theme.shadows[6] = "0px 12px 70px rgba(29, 39, 65, 0.16)";
theme.shadows[7] = "0px 12px 60px rgba(29, 39, 65, 0.18)";
theme.shadows[8] = "0px 20px 120px rgba(29, 39, 65, 0.18)";

theme.palette.secondary.main = colors.blueGrey[500];
theme.palette.secondary.dark = colors.blueGrey[900];
theme.palette.secondary.light = colors.blueGrey[100];

export const PatternLink = styled(Link)({
  color: patternLink,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
  "&:visited": {
    color: patternLink,
  },
});

export const ItemIconSelect = styled(Select)({
  "& div:first-of-type": {
    display: "flex",
    minWidth: "30px",
    alignItems: "center",
  },
});

export const EllipsisSX: SxProps<Theme> = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export const LinkSX: SxProps<Theme> = {
  color: patternLink,
  cursor: "pointer",
  fontWeight: "bold",
  "&:hover": {
    textDecoration: "underline",
  },
};

export const TransparentMenuItemSX: SxProps<Theme> = {
  "&.Mui-selected.Mui-selected": {
    backgroundColor: "transparent",
  },
};

export const SimpleHoverLinkSX: SxProps<Theme> = {
  cursor: "pointer",
  ":hover": {
    textDecoration: "underline",
  },
};

export const HoverLinkSX: SxProps<Theme> = {
  ...SimpleHoverLinkSX,
  color: "grey.900",
  ":hover": {
    color: patternLink,
    textDecoration: "underline",
  },
};

export const LeftStickyColumnSX: SxProps<Theme> = {
  position: "sticky",
  left: 0,
  background: "white",
  zIndex: 10,
};

export const TextareaStyle: CSSProperties = {
  width: "100%",
  fontFamily: "Inter,arial,sans-serif",
  border: "none",
  borderBottom: "1px solid #7598f6",
  outline: "none",
  caretColor: "#7598f6", // primary.main
  resize: "none",
};

export const ClickableIconSX: SxProps<Theme> = {
  cursor: "pointer",
  color: "grey.400",
  ":hover": {
    color: "grey.900",
  },
};

// This is a workaround for the data-pendo attribute not being recognized by the IconButton component
export interface DataPendoIconButtonProps extends IconButtonProps {
  "data-pendo"?: string;
}

export default theme;
